import { useRef } from 'react'

import { Box, type LabeledInputProps } from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'
import { reaction } from 'mobx'

import { observer } from 'mobx-react'

import InputText from 'modules/core/components/Inputs/Text'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'
import type User from 'modules/core/store/models/User'

interface Props {
  form: Form
  user: User
  isCurrentUser: boolean
  readonly?: boolean
  autofocus?: boolean
  mandatory?: boolean
  personalEmail: string
  employeeExists: boolean
  canEditPersonalEmail: boolean
  secondaryLabel?: LabeledInputProps['secondaryLabel']
}

const PersonalEmail = ({
  form,
  user,
  employeeExists,
  isCurrentUser,
  canEditPersonalEmail,
  readonly,
  autofocus,
  personalEmail,
  mandatory,
  secondaryLabel,
}: Props) => {
  const handler = useRef<(() => void) | undefined | null>(null)

  useEffect(() => {
    handler.current = reaction(
      () => personalEmail,
      () => {
        const email = form.get('personal_email')
        email.set(personalEmail || '')
        email.clean()
      },
      {
        fireImmediately: true,
      }
    )

    return () => {
      handler.current && handler.current()
      handler.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const isReadOnly = (): boolean => {
    if (user.isNew) return false

    const canNotEdit = employeeExists
      ? !canEditPersonalEmail
      : Boolean(!isCurrentUser && user.attributes.joined)

    return readonly || canNotEdit
  }

  const emailReadonlyTooltip = i18n.t('employee_form.personal_email.readonly_tooltip')

  const bangText: string | boolean | undefined = isReadOnly() ? emailReadonlyTooltip : mandatory

  return (
    <Box gap="s8">
      <InputText
        readonly={isReadOnly()}
        field={form.get('personal_email')}
        value={form.get('personal_email').value}
        label={i18n.t('employee_form.personal_email.label')}
        mandatory={!!bangText}
        autofocus={autofocus}
        secondaryLabel={secondaryLabel}
      />
    </Box>
  )
}

export default observer(PersonalEmail)
